import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Spinner, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdClose, MdSearch, MdClear, MdWarning } from 'react-icons/md';
import { FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getLocations } from 'api/partnerManagement';
import { updateChargingStation } from 'api/chargingStation';
import ReactPagination from 'components/general/ReactPagination';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import moment from 'moment';

const OcpiCredentialStationsModal = ({ show, onClose, ocpiData }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [stations, setStations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [updatingStationId, setUpdatingStationId] = useState(null);

  const ocpiId = _.get(ocpiData, 'id', '');
  const partnerName = _.get(ocpiData, 'partner_name', '') || _.get(ocpiData, 'partyId', '');
  const role = (_.get(ocpiData, 'role', '') || '').toUpperCase();
  const isEmsp = role === 'EMSP';

  const fetchAssociatedStations = useCallback(
    async (currentPage = 1, currentSearch = searchQuery) => {
      if (!ocpiId) return;
      setLoading(true);
      try {
        const payload = {
          id: ocpiId,
          page: currentPage,
          is_included_station: true,
          search: currentSearch,
          sortBy: 'createdAt:desc',
        };
        const res = await getLocations(payload);
        if (res && res.data) {
          const results = _.get(res.data, 'results', []);
          setStations(results);
          setTotalPages(_.get(res.data, 'totalPages', 1));
          setTotal(_.get(res.data, 'totalResults', results.length));
          setLimit(_.get(res.data, 'limit', 10));
          setPage(_.get(res.data, 'page', currentPage));
        }
      } catch (err) {
        toast.error(_.get(err, 'response.data.message', 'Failed to fetch associated stations'));
      } finally {
        setLoading(false);
      }
    },
    [ocpiId, searchQuery]
  );

  useEffect(() => {
    if (show && ocpiId) {
      setPage(1);
      fetchAssociatedStations(1, searchQuery);
    }
  }, [show, ocpiId]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAssociatedStations(1, searchQuery);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setPage(1);
    fetchAssociatedStations(1, '');
  };

  const checkChargerTariffError = (charger) => {
    if (charger.has_tariff_error !== undefined) {
      return charger.has_tariff_error;
    }
    const firstPlug = charger.plugs && charger.plugs.length > 0 ? charger.plugs[0] : null;
    const plugTariff = firstPlug ? firstPlug.tarrif : null;
    const chargerTariff = charger.tarrif;
    const effectiveTariff = plugTariff || chargerTariff;
    return !effectiveTariff;
  };

  const getChargerStatus = (ch) => {
    if (ch.plugs && ch.plugs.length > 0 && ch.plugs[0].status) {
      return ch.plugs[0].status;
    }
    if (ch.status) return ch.status;
    if (ch.is_active !== undefined) {
      return ch.is_active ? 'Active' : 'Inactive';
    }
    return 'Unknown';
  };

  const handleDownloadExcel = async () => {
    if (!ocpiId) return;
    setDownloadingExcel(true);
    try {
      const payload = {
        id: ocpiId,
        request_all: true,
        limit: 100000,
        is_included_station: true,
        search: searchQuery,
        sortBy: 'createdAt:desc',
      };
      const res = await getLocations(payload);
      const allStations = _.get(res, 'data.results', []);

      if (_.isEmpty(allStations)) {
        toast.info(t('No locations found to export.'));
        return;
      }

      const generatedDateStr = moment().format('YYYY-MM-DD HH:mm:ss');

      // Build Sheet Data (Array of Arrays) with Excel Generated Date on top
      const excelRows = [
        ['ASSOCIATED LOCATIONS & CHARGERS REPORT'],
        ['OCPI Partner:', partnerName],
        ['Report Generated Date:', generatedDateStr],
        [], // Blank row spacing
        [
          '#',
          'Station Name',
          'External Location ID',
          'Location Mongo ID',
          'Address / Area',
          'City',
          'State',
          'Country',
          'Show on Mobile App',
          'Show on CZ App',
          'Show to OCPI Partners',
          'Chargers (IDs & Status)',
          'Total Chargers',
          'Tariff Status',
          'Tariff Name(s)',
          'Tariff Price(s)',
          'External Tariff ID(s)',
          'Tariff Last Updated',
          'Created At',
        ],
      ];

      allStations.forEach((station, idx) => {
        const stId = station._id || station.id;
        const externalId = station.external_location_id || (station.station_id !== '0000' ? station.station_id : '-') || stId;
        const chargers = _.get(station, 'chargers', []);
        const chargerDetailsList = chargers.map((ch) => `${ch.charger_id || ch.id} (${getChargerStatus(ch)})`).join(', ') || 'No Chargers';
        const hasTariffErr = station.has_tariff_error || chargers.some(checkChargerTariffError);
        const createdAtStr = station.createdAt ? moment(station.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-';

        const tariffNames = chargers.map((ch) => _.get(ch, 'tariff_details.tariff_name', 'No Tariff')).filter((n) => n && n !== 'No Tariff').join('; ') || 'No Tariff';
        const tariffPrices = chargers.map((ch) => _.get(ch, 'tariff_details.price', '-')).filter((p) => p && p !== '-').join('; ') || '-';
        const externalTariffIds = chargers.map((ch) => _.get(ch, 'tariff_details.external_tariff_id', '-')).filter((id) => id && id !== '-').join('; ') || '-';
        const tariffLastUpdated = chargers.map((ch) => _.get(ch, 'tariff_details.last_updated', '-')).filter((d) => d && d !== '-').join('; ') || '-';

        excelRows.push([
          idx + 1,
          _.get(station, 'name', '-'),
          externalId,
          stId,
          _.get(station, 'area', '-'),
          _.get(station, 'city', '-'),
          _.get(station, 'state', '-'),
          _.get(station, 'country', '-'),
          station.show_on_mobile_app ? 'Yes' : 'No',
          station.show_on_cz_app ? 'Yes' : 'No',
          station.show_to_ocpi_partners ? 'Yes' : 'No',
          chargerDetailsList,
          chargers.length,
          hasTariffErr ? 'Tariff Missing / Invalid' : 'Valid Tariff',
          tariffNames,
          tariffPrices,
          externalTariffIds,
          tariffLastUpdated,
          createdAtStr,
        ]);
      });

      // Create Worksheet & Workbook
      const ws = XLSX.utils.aoa_to_sheet(excelRows);

      // Set Column Widths for readability
      ws['!cols'] = [
        { wch: 6 },  // #
        { wch: 30 }, // Station Name
        { wch: 25 }, // External Location ID
        { wch: 28 }, // Location Mongo ID
        { wch: 25 }, // Address / Area
        { wch: 18 }, // City
        { wch: 18 }, // State
        { wch: 15 }, // Country
        { wch: 20 }, // Show on Mobile App
        { wch: 18 }, // Show on CZ App
        { wch: 22 }, // Show to OCPI Partners
        { wch: 40 }, // Chargers (IDs & Status)
        { wch: 15 }, // Total Chargers
        { wch: 22 }, // Tariff Status
        { wch: 28 }, // Tariff Name(s)
        { wch: 22 }, // Tariff Price(s)
        { wch: 25 }, // External Tariff ID(s)
        { wch: 22 }, // Tariff Last Updated
        { wch: 22 }, // Created At
      ];

      const wb = { Sheets: { Locations: ws }, SheetNames: ['Locations'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });

      const safePartnerName = (partnerName || 'Partner').replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `OCPI_Locations_${safePartnerName}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
      FileSaver.saveAs(blob, fileName);
      toast.success(t('Locations Excel report downloaded successfully!'));
    } catch (err) {
      toast.error(_.get(err, 'response.data.message', 'Failed to generate Excel report'));
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleToggleVisibility = async (stationId, fieldName, currentValue) => {
    const newValue = !currentValue;
    setUpdatingStationId(`${stationId}_${fieldName}`);
    try {
      const payload = {
        id: stationId,
        [fieldName]: newValue,
      };
      const res = await updateChargingStation(payload);
      if (res && (res.status === 200 || res.status === 201)) {
        setStations((prevStations) =>
          prevStations.map((st) => (st.id === stationId || st._id === stationId ? { ...st, [fieldName]: newValue } : st))
        );
        toast.success(`Successfully updated ${fieldName === 'show_on_mobile_app' ? 'Mobile App' : 'CZ App'} visibility`);
      } else {
        toast.error('Failed to update visibility status');
      }
    } catch (err) {
      toast.error(_.get(err, 'response.data.message', 'Failed to update visibility status'));
    } finally {
      setUpdatingStationId(null);
    }
  };

  const handlePageClick = (pageVal) => {
    const nextPage = pageVal.selected + 1;
    setPage(nextPage);
    fetchAssociatedStations(nextPage, searchQuery);
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered backdrop="static" className="ocpi-stations-modal">
      <style>{`
        .ocpi-stations-modal-table tr:hover {
          background-color: inherit !important;
        }
        .ocpi-stations-modal-table th {
          color: #ffffff !important;
          background-color: #0f172a !important;
        }
      `}</style>
      <Modal.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#0f172a', color: '#ffffff', padding: '1rem 1.5rem', borderBottom: '1px solid #1e293b' }}>
        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: 600, color: '#ffffff' }}>
          {t('Associated Stations & Chargers')} - <span style={{ color: '#38bdf8' }}>{partnerName}</span>
        </Modal.Title>
        <span style={{ cursor: 'pointer', color: '#ffffff' }} onClick={onClose}>
          <MdClose size={26} />
        </span>
      </Modal.Header>
      
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', padding: '1.5rem', backgroundColor: '#f8fafc' }}>
        {/* Top Info Bar */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            {t('Report Generated Date')}: <span style={{ color: '#0f172a', fontWeight: 600 }}>{moment().format('YYYY-MM-DD HH:mm:ss')}</span>
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
            {t('Total Locations')}: <span style={{ color: '#0284c7', fontWeight: 600 }}>{total}</span>
          </div>
        </div>

        {/* Search Bar & Download Button Section */}
        <div className="mb-4">
          <form onSubmit={handleSearchSubmit}>
            <Row className="g-2 align-items-center">
              <Col md={6} lg={7}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <MdSearch style={{ position: 'absolute', left: '12px', color: '#64748b', fontSize: '20px', top: '11px' }} />
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('Search by location name, ID, or Mongo ID...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      paddingLeft: '38px',
                      paddingRight: searchQuery ? '38px' : '12px',
                      height: '42px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      fontSize: '14px',
                      color: '#0f172a',
                      backgroundColor: '#ffffff',
                    }}
                  />
                  {searchQuery && (
                    <MdClear
                      onClick={handleSearchClear}
                      style={{ position: 'absolute', right: '12px', color: '#64748b', fontSize: '20px', cursor: 'pointer', top: '11px' }}
                    />
                  )}
                </div>
              </Col>
              <Col md={3} lg={2}>
                <Button type="submit" className="w-100" style={{ height: '42px', padding: '0 1rem' }}>
                  {t('Search')}
                </Button>
              </Col>
              <Col md={3} lg={3}>
                <Button
                  type="button"
                  onClick={handleDownloadExcel}
                  disabled={downloadingExcel}
                  className="btn btn-success w-100 d-flex align-items-center justify-content-center"
                  style={{ height: '42px', backgroundColor: '#16a34a', borderColor: '#16a34a', color: '#ffffff', fontWeight: 600 }}
                >
                  {downloadingExcel ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('Exporting...')}
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="me-2" size={18} />
                      {t('Download Excel')}
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </form>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" style={{ width: '2.5rem', height: '2.5rem' }} />
            <div className="mt-3 text-muted font-weight-medium">{t('Loading stations...')}</div>
          </div>
        ) : _.isEmpty(stations) ? (
          <div className="text-center py-5" style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h5 className="text-dark font-weight-bold">{t('No associated stations found')}</h5>
            <p className="text-muted mb-0">{t('No stations matched your search query or criteria.')}</p>
          </div>
        ) : (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div className="table-responsive">
              <table className="table ocpi-stations-modal-table align-middle mb-0" style={{ color: '#0f172a' }}>
                <thead style={{ backgroundColor: '#0f172a', color: '#ffffff' }}>
                  <tr>
                    <th style={{ padding: '14px 16px', color: '#ffffff', backgroundColor: '#0f172a', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>#</th>
                    <th style={{ padding: '14px 16px', color: '#ffffff', backgroundColor: '#0f172a', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Station Name & ID')}</th>
                    <th style={{ padding: '14px 16px', color: '#ffffff', backgroundColor: '#0f172a', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Location Mongo ID')}</th>
                    <th style={{ padding: '14px 16px', color: '#ffffff', backgroundColor: '#0f172a', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Chargers & Status')}</th>
                    <th className="text-center" style={{ padding: '14px 16px', color: '#ffffff', backgroundColor: '#0f172a', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Show on Mobile App')}</th>
                    <th className="text-center" style={{ padding: '14px 16px', color: '#ffffff', backgroundColor: '#0f172a', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Show on CZ App')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stations.map((station, idx) => {
                    const stId = station._id || station.id;
                    const stationCode = station.external_location_id || (station.station_id !== '0000' ? station.station_id : null) || stId;
                    const showToOcpiPartners = station.show_to_ocpi_partners;
                    const chargers = _.get(station, 'chargers', []);
                    const isUpdatingMobile = updatingStationId === `${stId}_show_on_mobile_app`;
                    const isUpdatingCz = updatingStationId === `${stId}_show_on_cz_app`;

                    const stationHasTariffError = station.has_tariff_error || chargers.some(checkChargerTariffError);
                    const rowBgColor = idx % 2 === 0 ? '#ffffff' : '#f8fafc';

                    return (
                      <tr
                        key={stId || idx}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: rowBgColor,
                        }}
                      >
                        <td style={{ padding: '14px 16px', fontWeight: 500, color: '#64748b' }}>{(page - 1) * limit + idx + 1}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <div className="d-flex align-items-center">
                            <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '14px' }}>{_.get(station, 'name', '-')}</span>
                            {stationHasTariffError && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`st-tariff-tooltip-${stId}`}>
                                    {t('One or more chargers at this location have missing tariff configurations.')}
                                  </Tooltip>
                                }
                              >
                                <span className="ms-1" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                                  <MdWarning size={18} style={{ color: '#f59e0b' }} />
                                </span>
                              </OverlayTrigger>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#0284c7', fontWeight: 500 }}>ID: {stationCode}</div>
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, marginTop: '2px' }}>
                            show_to_ocpi_partners:{' '}
                            <span style={{ color: showToOcpiPartners ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                              {showToOcpiPartners !== undefined ? String(showToOcpiPartners) : 'false'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ fontFamily: 'monospace', backgroundColor: '#f1f5f9', color: '#334155', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '12px', fontWeight: 600 }}>
                            {stId}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          {!_.isEmpty(chargers) ? (
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                              {chargers.map((ch, cIdx) => {
                                const isTariffError = checkChargerTariffError(ch);
                                const status = getChargerStatus(ch);
                                const chargerName = ch.charger_id || ch.id;

                                let badgeStyle = {
                                  backgroundColor: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #bfdbfe',
                                };

                                if (['AVAILABLE', 'ACTIVE'].includes(status.toUpperCase())) {
                                  badgeStyle = {
                                    backgroundColor: '#f0fdf4',
                                    color: '#15803d',
                                    border: '1px solid #bbf7d0',
                                  };
                                } else if (['CHARGING', 'PREPARING', 'FINISHING', 'SUSPENDEDEV', 'SUSPENDEDEVSE'].includes(status.toUpperCase())) {
                                  badgeStyle = {
                                    backgroundColor: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                  };
                                } else if (['UNAVAILABLE', 'FAULTED', 'INOPERATIVE'].includes(status.toUpperCase())) {
                                  badgeStyle = {
                                    backgroundColor: '#fff7ed',
                                    color: '#c2410c',
                                    border: '1px solid #ffedd5',
                                  };
                                }

                                return (
                                  <div key={ch.id || ch._id || cIdx} className="d-inline-flex align-items-center me-1 mb-1">
                                    <span
                                      style={{
                                        ...badgeStyle,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        padding: '3px 8px',
                                        borderRadius: '4px',
                                        display: 'inline-block',
                                      }}
                                    >
                                      {chargerName} [{status}]
                                    </span>
                                    {isTariffError && (
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip id={`ch-tariff-tooltip-${stId}-${cIdx}`}>
                                            <div style={{ textAlign: 'left', padding: '2px', fontSize: '12px' }}>
                                              <strong style={{ color: '#fca5a5' }}>⚠️ Tariff Issue Details:</strong>
                                              <div>• Charger: {chargerName}</div>
                                              <div>• Status: {status}</div>
                                              <div>• Tariff Name: {_.get(ch, 'tariff_details.tariff_name', 'Not Configured')}</div>
                                              <div>• Price: {_.get(ch, 'tariff_details.price', 'Missing')}</div>
                                              <div>• External Tariff ID: {_.get(ch, 'tariff_details.external_tariff_id', 'None')}</div>
                                            </div>
                                          </Tooltip>
                                        }
                                      >
                                        <span style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', marginLeft: '3px' }}>
                                          <MdWarning size={16} style={{ color: '#ef4444' }} />
                                        </span>
                                      </OverlayTrigger>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="text-muted" style={{ fontSize: '12px' }}>{t('No Chargers')}</span>
                          )}
                        </td>
                        <td className="text-center" style={{ padding: '14px 16px' }}>
                          <div className="d-flex justify-content-center align-items-center">
                            {isUpdatingMobile ? (
                              <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                              <Form.Check
                                type="switch"
                                id={`switch-mobile-${stId}`}
                                checked={Boolean(station.show_on_mobile_app)}
                                disabled={isEmsp}
                                onChange={() => {
                                  if (!isEmsp) {
                                    handleToggleVisibility(stId, 'show_on_mobile_app', station.show_on_mobile_app);
                                  }
                                }}
                                label=""
                                style={{ cursor: isEmsp ? 'not-allowed' : 'pointer', transform: 'scale(1.1)' }}
                              />
                            )}
                          </div>
                        </td>
                        <td className="text-center" style={{ padding: '14px 16px' }}>
                          <div className="d-flex justify-content-center align-items-center">
                            {isUpdatingCz ? (
                              <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                              <Form.Check
                                type="switch"
                                id={`switch-cz-${stId}`}
                                checked={Boolean(station.show_on_cz_app)}
                                disabled={isEmsp}
                                onChange={() => {
                                  if (!isEmsp) {
                                    handleToggleVisibility(stId, 'show_on_cz_app', station.show_on_cz_app);
                                  }
                                }}
                                label=""
                                style={{ cursor: isEmsp ? 'not-allowed' : 'pointer', transform: 'scale(1.1)' }}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="d-flex justify-content-center p-3" style={{ borderTop: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                <ReactPagination
                  currentPage={page}
                  limit={limit}
                  total={total}
                  handlePageClick={handlePageClick}
                  totalPages={totalPages}
                  marginPagesDisplayed={1}
                />
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', padding: '0.75rem 1.5rem' }}>
        <Button onClick={onClose} className="btn btn-secondary">
          {t('Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

OcpiCredentialStationsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ocpiData: PropTypes.object,
};

export default OcpiCredentialStationsModal;
